import { orderStatusLabelPtBr, tenantShopStatusLabelPtBr } from './orderStatus.js';

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function moneyBr(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n);
}

function periodLabel(days) {
  if (days === 0) return 'Todo o período';
  if (days === 7) return 'Últimos 7 dias';
  if (days === 30) return 'Últimos 30 dias';
  if (days === 90) return 'Últimos 90 dias';
  if (days === 365) return 'Últimos 365 dias';
  return `${days} dias`;
}

function sumOrders(rows) {
  let count = 0;
  let revenue = 0;
  for (const r of rows || []) {
    count += Number(r.cnt || r.count || 0);
    revenue += Number(r.revenue || 0);
  }
  return { count, revenue };
}

/**
 * Abre uma página imprimível (Ctrl+P → Guardar como PDF). Sem dependências; UTF-8 correto.
 * @param {Record<string, unknown>} report — resposta de GET …/tenants/:id/report (admin ou painel empresa)
 * @param {{ variant?: 'admin' | 'tenant' }} [opts]
 */
export function openTenantReportPrintWindow(report, opts = {}) {
  const variant = opts.variant === 'tenant' ? 'tenant' : 'admin';
  const tenant = report?.tenant && typeof report.tenant === 'object' ? report.tenant : {};
  const days = Number(report?.period_days ?? 30);
  const generated = report?.generated_at
    ? new Date(String(report.generated_at)).toLocaleString('pt-BR')
    : new Date().toLocaleString('pt-BR');

  const prod = report?.products && typeof report.products === 'object' ? report.products : {};
  const ordersAll = Array.isArray(report?.orders_by_status_all) ? report.orders_by_status_all : [];
  const ordersPeriod = Array.isArray(report?.orders_by_status_period) ? report.orders_by_status_period : [];
  const recent = Array.isArray(report?.recent_orders) ? report.recent_orders.slice(0, 40) : [];
  const sub = report?.saas_subscription && typeof report.saas_subscription === 'object' ? report.saas_subscription : {};

  const totAll = sumOrders(ordersAll);
  const totPer = sumOrders(ordersPeriod);

  const rowsAll = ordersAll
    .map(
      (r) =>
        `<tr><td>${escapeHtml(orderStatusLabelPtBr(r.status))}</td><td class="num">${escapeHtml(String(r.cnt ?? ''))}</td><td class="num">${escapeHtml(moneyBr(r.revenue))}</td></tr>`,
    )
    .join('');

  const rowsPer = ordersPeriod
    .map(
      (r) =>
        `<tr><td>${escapeHtml(orderStatusLabelPtBr(r.status))}</td><td class="num">${escapeHtml(String(r.cnt ?? ''))}</td><td class="num">${escapeHtml(moneyBr(r.revenue))}</td></tr>`,
    )
    .join('');

  const rowsRecent = recent
    .map(
      (o) =>
        `<tr><td>${escapeHtml(o.id)}</td><td>${escapeHtml(String(o.created_at || '').slice(0, 19))}</td><td>${escapeHtml(o.customer_nome)}</td><td>${escapeHtml(orderStatusLabelPtBr(o.status))}</td><td class="num">${escapeHtml(moneyBr(o.total))}</td><td>${escapeHtml(o.payment_method_code || '—')}</td></tr>`,
    )
    .join('');

  const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="utf-8"/>
<title>Relatório — ${escapeHtml(tenant.nome || 'Empresa')}</title>
<style>
  body { font-family: system-ui, Segoe UI, sans-serif; margin: 24px; color: #111; font-size: 12px; }
  h1 { font-size: 18px; margin: 0 0 4px; }
  .muted { color: #555; font-size: 11px; margin-bottom: 16px; }
  h2 { font-size: 13px; margin: 20px 0 8px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
  th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
  th { background: #f4f4f4; font-weight: 600; }
  td.num { text-align: right; font-variant-numeric: tabular-nums; }
  .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 16px; }
  .card { border: 1px solid #ddd; border-radius: 8px; padding: 10px; }
  .card strong { display: block; font-size: 11px; color: #666; text-transform: uppercase; }
  .card span { font-size: 18px; font-weight: 700; }
  @media print {
    body { margin: 12mm; }
    .no-print { display: none; }
  }
</style>
</head>
<body>
  <p class="no-print"><button type="button" onclick="window.print()" style="padding:10px 18px;font-size:14px;cursor:pointer;border-radius:8px;border:1px solid #333;background:#111;color:#fff">Imprimir ou guardar PDF</button></p>
  <p class="no-print muted">Ou use Ctrl+P e escolha <strong>Guardar como PDF</strong> / Microsoft Print to PDF.</p>
  <h1>${variant === 'tenant' ? 'Relatório operacional' : 'Relatório operacional (admin)'}</h1>
  <div class="muted">
    ${escapeHtml(tenant.nome || '')} · slug /${escapeHtml(tenant.slug || '')} · empresa #${escapeHtml(String(tenant.id ?? ''))}<br/>
    Gerado: ${escapeHtml(generated)} · Janela pedidos: ${escapeHtml(periodLabel(days))}
  </div>

  <div class="grid">
    <div class="card"><strong>Produtos (total)</strong><span>${escapeHtml(String(prod.total ?? 0))}</span></div>
    <div class="card"><strong>Visíveis na loja</strong><span>${escapeHtml(String(prod.active_visible ?? 0))}</span></div>
    <div class="card"><strong>Clientes</strong><span>${escapeHtml(String(report?.customers ?? 0))}</span></div>
    <div class="card"><strong>Categorias ERP</strong><span>${escapeHtml(String(report?.erp_categories ?? 0))}</span></div>
    <div class="card"><strong>Stock controlado (prod.)</strong><span>${escapeHtml(String(prod.stock_tracked ?? 0))}</span></div>
    <div class="card"><strong>SaaS em aberto</strong><span>${escapeHtml(String(sub.pending_charges ?? 0))}</span>
      <div style="font-size:11px;font-weight:600">${escapeHtml(moneyBr(sub.pending_amount_brl))}</div></div>
  </div>

  <p><strong>Responsável:</strong> ${escapeHtml(tenant.owner_email || '—')} · <strong>Estado da loja:</strong> ${escapeHtml(tenantShopStatusLabelPtBr(tenant.status))}
  · <strong>Pagamento online:</strong> ${Number(tenant.payment_online_enabled) === 1 ? 'Sim' : 'Não'}
  · <strong>Só catálogo:</strong> ${Number(tenant.catalog_only_mode) === 1 ? 'Sim' : 'Não'}
  · <strong>Bloquear sem stock:</strong> ${Number(tenant.block_sale_zero_stock) === 1 ? 'Sim' : 'Não'}</p>

  <h2>Pedidos por estado (histórico completo)</h2>
  <table>
    <thead><tr><th>Estado</th><th>Quantidade</th><th>Total</th></tr></thead>
    <tbody>${rowsAll || '<tr><td colspan="3">Nenhum pedido.</td></tr>'}</tbody>
    <tfoot><tr><th>Total</th><th class="num">${escapeHtml(String(totAll.count))}</th><th class="num">${escapeHtml(moneyBr(totAll.revenue))}</th></tr></tfoot>
  </table>

  <h2>Pedidos por estado (${escapeHtml(periodLabel(days))})</h2>
  <table>
    <thead><tr><th>Estado</th><th>Quantidade</th><th>Total</th></tr></thead>
    <tbody>${rowsPer || '<tr><td colspan="3">Sem pedidos nesta janela.</td></tr>'}</tbody>
    <tfoot><tr><th>Total período</th><th class="num">${escapeHtml(String(totPer.count))}</th><th class="num">${escapeHtml(moneyBr(totPer.revenue))}</th></tr></tfoot>
  </table>

  <h2>Últimos pedidos (amostra)</h2>
  <table>
    <thead><tr><th>ID</th><th>Data</th><th>Cliente</th><th>Estado</th><th>Total</th><th>Pagamento</th></tr></thead>
    <tbody>${rowsRecent || '<tr><td colspan="6">—</td></tr>'}</tbody>
  </table>

  <p class="muted">${variant === 'tenant' ? 'OminIA Pedidos — relatório da sua empresa' : 'OminIA Pedidos — documento interno super_admin'}</p>
</body>
</html>`;

  // Não use `noopener` em `window.open` aqui: no Chrome devolve `null` e abre um separador
  // `about:blank` que o script não pode preencher com `document.write` — fica em branco.
  const w = window.open('', '_blank');
  if (!w) {
    window.alert('Permita pop-ups para imprimir / guardar PDF.');
    return;
  }
  try {
    w.opener = null;
  } catch {
    /* ignore */
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
}
